import { useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { signIn } from '../core/api/auth-service';
import { classifyIdentifier } from '../core/auth/sign-in-identifier';
import type { SignInIdentifierKind } from '../core/auth/sign-in-identifier-kind';
import { describeApiError } from '../core/errors/describe-api-error';
import { describeFieldErrors } from '../core/errors/describe-field-errors';
import type { TranslationKey } from '../core/i18n/translation';
import { useLanguage } from '../core/hooks/use-language';
import { useTranslation } from '../core/hooks/use-translation';
import { Alert } from '../ui/alert';
import { Button } from '../ui/button';
import { AuthSurface } from './auth/auth-surface';
import { TextField } from './auth/text-field';

/** Which of the three the customer typed, said back to them under the field. */
const KIND_KEYS: Record<Exclude<SignInIdentifierKind, 'unknown'>, TranslationKey> = {
    email: 'auth.sign_in.kind.email',
    phone: 'auth.sign_in.kind.phone',
    iin: 'auth.sign_in.kind.iin'
};

export function SignInPage() {
    const { t } = useTranslation();
    const language = useLanguage();
    const navigate = useNavigate();

    const [identifier, setIdentifier] = useState('');
    const [password, setPassword] = useState('');
    const [errorKey, setErrorKey] = useState<TranslationKey | null>(null);
    const [fieldErrors, setFieldErrors] = useState<Record<string, TranslationKey>>({});
    const [submitting, setSubmitting] = useState(false);

    const kind = classifyIdentifier(identifier);

    async function onSubmit(event: FormEvent) {
        event.preventDefault();
        setErrorKey(null);
        setFieldErrors({});

        // Refused here as well as by the server, so a value the product cannot look anything up by
        // never becomes a failed sign-in attempt the customer has to interpret. It says nothing
        // about who has an account — the shape of the text is all it reads.
        if (kind === 'unknown') {
            setFieldErrors({ identifier: 'auth.sign_in.identifier_invalid' });
            return;
        }

        setSubmitting(true);

        try {
            await signIn({ identifier: identifier.trim(), password });
            void navigate(`/${language}/portal`, { replace: true });
        } catch (error) {
            // The banner gives way to the fields when the server named them: repeating the
            // complaint above a form that is now marking the box reads as two separate faults.
            const fields = describeFieldErrors(error);

            setFieldErrors(fields);
            setErrorKey(Object.keys(fields).length > 0 ? null : describeApiError(error));
        } finally {
            setSubmitting(false);
        }
    }

    return (
        <AuthSurface>
            <h1 className="text-2xl font-extrabold text-slate-900 dark:text-slate-100">
                {t('auth.sign_in.title')}
            </h1>
            <p className="mt-2 text-sm text-slate-500 dark:text-slate-400">{t('auth.sign_in.subtitle')}</p>

            <form className="mt-7 space-y-4" onSubmit={(event) => void onSubmit(event)}>
                <TextField
                    label={t('auth.sign_in.identifier')}
                    type="text"
                    value={identifier}
                    // `username` rather than `email`: a password manager should offer whichever of
                    // the three it saved, and `email` makes it offer only addresses.
                    autoComplete="username"
                    inputMode={kind === 'email' ? 'email' : 'text'}
                    error={fieldErrors['identifier']}
                    hint={
                        kind === 'unknown'
                            ? t('auth.sign_in.identifier_hint')
                            : `${t('auth.sign_in.recognised')} ${t(KIND_KEYS[kind])}`
                    }
                    onChange={setIdentifier}
                />
                <TextField
                    label={t('auth.sign_in.password')}
                    type="password"
                    value={password}
                    autoComplete="current-password"
                    error={fieldErrors['password']}
                    onChange={setPassword}
                />

                {errorKey !== null && <Alert>{t(errorKey)}</Alert>}

                <Button type="submit" size="lg" block disabled={submitting}>
                    {submitting ? t('common.loading') : t('auth.sign_in.submit')}
                </Button>
            </form>

            <p className="mt-6 border-t border-slate-200 pt-5 text-sm text-slate-500 dark:border-slate-800 dark:text-slate-400">
                {t('auth.sign_in.no_account')}{' '}
                <Link
                    className="font-semibold text-brand-700 hover:underline dark:text-brand-400"
                    to={`/${language}/account/register`}
                >
                    {t('auth.sign_in.register')}
                </Link>
            </p>
        </AuthSurface>
    );
}
